import * as THREE from 'three'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Experience from './Experience/Experience.js'
import { SCENTS } from './data/scents.js'

gsap.registerPlugin(ScrollTrigger)

const CARD_SIZE = 240

/**
 * LidScene
 * Builds the 15 scent card grid in #scents-grid.
 *
 * Uses a SINGLE shared WebGLRenderer to stay within the browser's
 * WebGL context limit (~16 per page). Each card gets its own Three.js
 * Scene + Camera, and the shared renderer blits each frame into the
 * card's 2D canvas via drawImage (preserveDrawingBuffer: true).
 *
 * Only currently-visible cards are rendered each frame
 * (IntersectionObserver adds/removes from this.visibleCards).
 */
export default class LidScene {
    constructor() {
        this.experience = new Experience()
        this.resources  = this.experience.resources

        this.cards        = []
        this.visibleCards = new Set()

        // One shared WebGL renderer — not attached to the DOM.
        // preserveDrawingBuffer is required so drawImage reads correct pixels.
        this.renderer = new THREE.WebGLRenderer({
            antialias:             true,
            alpha:                 true,
            preserveDrawingBuffer: true,
        })
        this.renderer.setSize(CARD_SIZE, CARD_SIZE)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        this.renderer.setClearColor(0x000000, 0)
        this.renderer.toneMapping         = THREE.CineonToneMapping
        this.renderer.toneMappingExposure = 1.5

        this.buildGrid()
        this.setupFilter()
        this.setupScrollReveal()
        this.startLoop()
    }

    buildGrid() {
        const grid = document.getElementById('scents-grid')
        SCENTS.forEach((scent) => {
            const card = this.buildCard(scent)
            grid.appendChild(card.el)
            this.cards.push(card)
        })
    }

    buildCard(scent) {
        const el = document.createElement('div')
        el.className = 'scent-card'
        el.dataset.category = scent.category

        const wrap = document.createElement('div')
        wrap.className = 'scent-card__canvas-wrap'

        const canvas = document.createElement('canvas')
        canvas.width  = CARD_SIZE
        canvas.height = CARD_SIZE
        const ctx2d = canvas.getContext('2d')
        wrap.appendChild(canvas)

        // Center icon in the visual area (shown when card is closed)
        const centerIcon = document.createElement('img')
        centerIcon.className = 'scent-card__icon scent-card__icon--center'
        centerIcon.src = `/textures/icons/${scent.lid}.png`
        centerIcon.alt = scent.name
        wrap.appendChild(centerIcon)

        // Description overlay — revealed on card click
        const overlay = document.createElement('div')
        overlay.className = 'scent-card__desc-overlay'
        overlay.innerHTML = `<p class="scent-card__desc">${scent.desc}</p>`
        wrap.appendChild(overlay)

        el.appendChild(wrap)

        const info = document.createElement('div')
        info.className = 'scent-card__info'
        info.innerHTML = `
            <span class="scent-card__badge scent-card__badge--${scent.category}">${scent.category}</span>
            <div class="scent-card__name">${scent.name}</div>
            <div class="scent-card__notes">${scent.notes.join(' · ')}</div>
            ${scent.series ? `<div class="scent-card__series">${scent.series}</div>` : ''}
        `
        el.appendChild(info)

        // Toggle description overlay on click
        el.addEventListener('click', () => el.classList.toggle('scent-card--open'))

        const { scene3d, camera3d, model } = this.buildMiniScene(scent)

        const cardData = { el, scene3d, camera3d, model, ctx2d, scent, isHovered: false }

        this.setupTilt(cardData)
        this.setupObserver(cardData)

        return cardData
    }

    buildMiniScene(scent) {
        const scene3d = new THREE.Scene()

        const camera3d = new THREE.PerspectiveCamera(40, 1, 0.01, 100)
        camera3d.position.set(0, 0.1, 3)

        // Lighting
        const ambient = new THREE.AmbientLight('#7a5c3c', 1.2)
        scene3d.add(ambient)

        const dirLight = new THREE.DirectionalLight('#ffe0b0', 2.5)
        dirLight.position.set(2, 3, 3)
        scene3d.add(dirLight)

        const fillLight = new THREE.DirectionalLight('#b07cc6', 0.5)
        fillLight.position.set(-2, -1, 2)
        scene3d.add(fillLight)

        // Lid model
        let model = null
        const gltf = this.resources.items[scent.lid]

        if (gltf) {
            model = gltf.scene.clone()
            model.scale.set(0.08, 0.08, 0.08)
            model.position.set(0, 0, 0)

            // DoubleSide on all meshes — lid is a flat plane
            model.traverse((child) => {
                if (child.isMesh && child.material) {
                    const mats = Array.isArray(child.material)
                        ? child.material
                        : [child.material]
                    mats.forEach((mat) => {
                        mat.side = THREE.DoubleSide
                        mat.envMapIntensity = 0.3
                    })
                }
            })

            scene3d.add(model)
        }

        return { scene3d, camera3d, model }
    }

    setupTilt(cardData) {
        const { el, model } = cardData
        const MAX_TILT = 12

        el.addEventListener('mousemove', (e) => {
            cardData.isHovered = true
            const rect = el.getBoundingClientRect()
            const x = (e.clientX - rect.left) / rect.width  - 0.5
            const y = (e.clientY - rect.top)  / rect.height - 0.5

            el.style.transform = `perspective(600px) rotateY(${x * MAX_TILT * 2}deg) rotateX(${-y * MAX_TILT}deg)`

            if (model) {
                model.rotation.y = x * 0.4
                model.rotation.x = -y * 0.2
            }
        })

        el.addEventListener('mouseleave', () => {
            cardData.isHovered = false
            el.style.transform = 'perspective(600px) rotateY(0deg) rotateX(0deg)'
            if (model) {
                gsap.to(model.rotation, { x: 0, y: 0, duration: 0.6, ease: 'power2.out' })
            }
        })
    }

    setupObserver(cardData) {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    this.visibleCards.add(cardData)
                } else {
                    this.visibleCards.delete(cardData)
                }
            },
            { threshold: 0.1 }
        )
        observer.observe(cardData.el)
    }

    setupFilter() {
        const buttons = document.querySelectorAll('.filter-btn')

        buttons.forEach((btn) => {
            btn.addEventListener('click', () => {
                buttons.forEach((b) => b.classList.remove('filter-btn--active'))
                btn.classList.add('filter-btn--active')

                const filter = btn.dataset.filter

                this.cards.forEach(({ el }) => {
                    if (filter === 'all' || el.dataset.category === filter) {
                        el.classList.remove('scent-card--hidden')
                    } else {
                        el.classList.add('scent-card--hidden')
                    }
                })

                ScrollTrigger.refresh()
            })
        })
    }

    setupScrollReveal() {
        gsap.to('.scent-card', {
            scrollTrigger: {
                trigger: '#scents-grid',
                start: 'top 85%',
            },
            opacity: 1,
            y: 0,
            stagger: 0.05,
            duration: 0.6,
            ease: 'power2.out',
        })
    }

    startLoop() {
        const gl = this.renderer.domElement

        const loop = (ts) => {
            const t = ts * 0.001

            this.visibleCards.forEach((cardData) => {
                const { scene3d, camera3d, model, ctx2d, isHovered } = cardData
                if (model && !isHovered) {
                    model.rotation.y = Math.sin(t * 0.4) * 0.3
                }
                this.renderer.render(scene3d, camera3d)
                ctx2d.clearRect(0, 0, CARD_SIZE, CARD_SIZE)
                ctx2d.drawImage(gl, 0, 0, CARD_SIZE, CARD_SIZE)
            })

            requestAnimationFrame(loop)
        }
        requestAnimationFrame(loop)
    }
}
